import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { customerReferralLink } from "../../lib/urls";
import { toast } from "sonner";
import {
    Gift, Copy, Share2, Mail, MessageCircle, Wallet, Users,
    CheckCircle2, ArrowDownCircle, ArrowUpCircle,
} from "lucide-react";

const REF_STATUS = {
    pending: { label: "Pendente", color: "bg-gold-soft/20 text-gold-deep" },
    awaiting_sale: { label: "Aguardando compra", color: "bg-wine/10 text-wine" },
    qualified: { label: "Qualificado", color: "bg-vineyard text-cream" },
    reversed: { label: "Revertido", color: "bg-destructive/15 text-destructive" },
};

const TX_STATUS = {
    available: "Disponível", used: "Utilizado", reserved: "Reservado",
    reversed: "Revertido", expired: "Expirado",
};

export default function ClientReferrals() {
    const [data, setData] = useState(null);
    const [notifs, setNotifs] = useState([]);

    useEffect(() => {
        api.get("/customer/rewards").then((r) => setData(r.data)).catch(() => {});
        api.get("/notifications").then((r) => setNotifs(((r.data?.items) || []).filter((n) => n.type === "reward_credit" && !n.read))).catch(() => {});
    }, []);

    const dismiss = async (id) => {
        try { await api.post(`/notifications/${id}/read`); } catch { /* ignore */ }
        setNotifs((ns) => ns.filter((n) => n.id !== id));
    };

    if (!data) return <p className="text-wine-dark/60 label-overline">A carregar…</p>;

    const code = data.code || "—";
    const url = customerReferralLink(data.code) || data.url || "";
    const copy = async (text, label) => { await navigator.clipboard.writeText(text); toast.success(`${label} copiado`); };
    const msg = `Descobre os melhores vinhos e produtos artesanais portugueses no Caseirinho — usa o meu convite e ganha desconto na 1ª compra: ${url}`;
    const wa = `https://wa.me/?text=${encodeURIComponent(msg)}`;
    const mail = `mailto:?subject=${encodeURIComponent("Convite Caseirinho")}&body=${encodeURIComponent(msg)}`;
    const nativeShare = async () => {
        if (navigator.share) { try { await navigator.share({ title: "Caseirinho", text: msg, url }); } catch { /* cancelled */ } }
        else copy(url, "Link");
    };

    return (
        <section className="space-y-6" data-testid="client-referrals">
            <div>
                <span className="label-overline text-wine/60 inline-flex items-center gap-1.5"><Gift className="h-3 w-3" /> Programa de indicações</span>
                <h1 className="font-heading text-4xl text-wine-dark">Partilhe o Caseirinho. Ganhe benefícios.</h1>
                <p className="text-sm text-wine-dark/60 mt-2 max-w-xl">{data.message}</p>
            </div>

            {notifs.map((n) => (
                <div key={n.id} className="bg-vineyard/10 border border-vineyard/30 rounded-md px-4 py-3 flex items-start justify-between gap-3" data-testid={`reward-notif-${n.id}`}>
                    <div>
                        <span className="label-overline text-vineyard text-[10px]">{n.title}</span>
                        <p className="text-sm text-wine-dark/80 mt-0.5">{n.message}</p>
                    </div>
                    <button onClick={() => dismiss(n.id)} className="text-wine-dark/50 hover:text-wine text-xs label-overline" data-testid={`reward-notif-dismiss-${n.id}`}>Dispensar</button>
                </div>
            ))}

            {/* Wallet balance */}
            <div className="bg-wine-dark text-cream rounded-md p-6 flex items-center justify-between" data-testid="wallet-card">
                <div>
                    <span className="label-overline text-gold-soft inline-flex items-center gap-1.5"><Wallet className="h-3.5 w-3.5" /> Saldo Caseirinho</span>
                    <p className="font-heading text-5xl mt-1" data-testid="wallet-balance">€{data.balance.toFixed(2)}</p>
                    <p className="text-xs text-cream/55 mt-1">Utilizável nas suas próximas compras · não convertível em dinheiro</p>
                </div>
                <div className="hidden md:grid grid-cols-2 gap-x-8 gap-y-3 text-right">
                    <Stat label="Amigos convidados" value={data.stats.invited} />
                    <Stat label="Compras qualificadas" value={data.stats.qualified} />
                    <Stat label="Créditos recebidos" value={`€${data.stats.credits_received.toFixed(2)}`} />
                    <Stat label="Créditos utilizados" value={`€${data.stats.credits_used.toFixed(2)}`} />
                </div>
            </div>

            {/* How it works */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white border border-gold-soft/30 rounded-md p-5">
                    <span className="label-overline text-wine">O seu amigo ganha</span>
                    <p className="font-heading text-2xl text-wine-dark mt-1">{data.program.friend_discount_pct}% de desconto</p>
                    <p className="text-xs text-wine-dark/60 mt-1">na primeira compra dele.</p>
                </div>
                <div className="bg-white border border-gold-soft/30 rounded-md p-5">
                    <span className="label-overline text-wine">Você ganha</span>
                    <p className="font-heading text-2xl text-vineyard mt-1">€{data.program.referrer_credit_eur.toFixed(2)} de crédito</p>
                    <p className="text-xs text-wine-dark/60 mt-1">após a compra válida do amigo · validade {data.program.credit_validity_months} meses.</p>
                </div>
            </div>

            {/* Share */}
            <div className="bg-white border border-gold-soft/30 rounded-md p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <span className="label-overline text-wine-dark/65 text-[10px] block mb-1.5">O seu código</span>
                        <div className="flex items-center gap-2">
                            <code className="flex-1 bg-cream border border-wine-dark/15 rounded-md px-3 py-2.5 text-sm font-mono text-wine-dark" data-testid="client-referral-code">{code}</code>
                            <button onClick={() => copy(code, "Código")} className="btn-wine-outline text-xs px-3 py-2.5" data-testid="copy-code"><Copy className="h-4 w-4" /></button>
                        </div>
                    </div>
                    <div>
                        <span className="label-overline text-wine-dark/65 text-[10px] block mb-1.5">O seu link</span>
                        <div className="flex items-center gap-2">
                            <input readOnly value={url} className="flex-1 bg-cream border border-wine-dark/15 rounded-md px-3 py-2.5 text-sm text-wine-dark truncate" data-testid="client-referral-url" />
                            <button onClick={() => copy(url, "Link")} className="btn-wine-outline text-xs px-3 py-2.5" data-testid="copy-link"><Copy className="h-4 w-4" /></button>
                        </div>
                    </div>
                </div>
                <div className="flex flex-wrap gap-2 pt-1">
                    <button onClick={() => copy(url, "Link")} className="inline-flex items-center gap-2 text-xs label-overline bg-wine text-cream px-4 py-2.5 rounded-md hover:bg-wine-dark" data-testid="share-copy"><Copy className="h-3.5 w-3.5" /> Copiar</button>
                    <a href={wa} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-xs label-overline bg-vineyard text-cream px-4 py-2.5 rounded-md hover:opacity-90" data-testid="share-whatsapp"><MessageCircle className="h-3.5 w-3.5" /> WhatsApp</a>
                    <a href={mail} className="inline-flex items-center gap-2 text-xs label-overline border border-wine-dark/20 text-wine px-4 py-2.5 rounded-md hover:border-wine" data-testid="share-email"><Mail className="h-3.5 w-3.5" /> Email</a>
                    <button onClick={nativeShare} className="inline-flex items-center gap-2 text-xs label-overline border border-wine-dark/20 text-wine px-4 py-2.5 rounded-md hover:border-wine" data-testid="share-native"><Share2 className="h-3.5 w-3.5" /> Partilhar</button>
                </div>
            </div>

            {/* Invited friends */}
            <div className="bg-white border border-gold-soft/30 rounded-md overflow-hidden">
                <div className="px-5 py-4 border-b border-wine-dark/10">
                    <h2 className="font-heading text-xl text-wine-dark inline-flex items-center gap-2"><Users className="h-4 w-4 text-wine" /> Amigos convidados</h2>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="invited-friends">
                        <thead><tr className="text-left label-overline text-wine-dark/60 border-b border-wine-dark/10 bg-cream/40"><th className="py-3 px-4">Amigo</th><th>Data</th><th>Estado</th><th>Recompensa</th></tr></thead>
                        <tbody>
                            {data.referrals.map((r) => {
                                const m = REF_STATUS[r.status] || REF_STATUS.pending;
                                return (
                                    <tr key={r.id} className="border-t border-wine-dark/5" data-testid={`client-referral-${r.id}`}>
                                        <td className="py-3 px-4 text-wine-dark">{r.referred_name || "—"}</td>
                                        <td className="text-xs text-wine-dark/60">{new Date(r.created_at).toLocaleDateString("pt-PT")}</td>
                                        <td><span className={`inline-flex items-center label-overline px-2 py-1 rounded text-[10px] ${m.color}`}>{m.label}</span></td>
                                        <td className="text-xs">{r.status === "qualified" ? <span className="text-vineyard inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> €{data.program.referrer_credit_eur.toFixed(2)}</span> : <span className="text-wine-dark/40">—</span>}</td>
                                    </tr>
                                );
                            })}
                            {data.referrals.length === 0 && <tr><td colSpan="4" className="text-center py-8 text-wine-dark/60">Ainda sem convites. Partilhe o seu link!</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Wallet ledger */}
            <div className="bg-white border border-gold-soft/30 rounded-md overflow-hidden">
                <div className="px-5 py-4 border-b border-wine-dark/10">
                    <h2 className="font-heading text-xl text-wine-dark inline-flex items-center gap-2"><Wallet className="h-4 w-4 text-wine" /> Movimentos da carteira</h2>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="wallet-ledger">
                        <thead><tr className="text-left label-overline text-wine-dark/60 border-b border-wine-dark/10 bg-cream/40"><th className="py-3 px-4">Descrição</th><th>Data</th><th>Estado</th><th className="text-right pr-4">Valor</th></tr></thead>
                        <tbody>
                            {data.transactions.map((t) => (
                                <tr key={t.id} className="border-t border-wine-dark/5" data-testid={`wallet-tx-${t.id}`}>
                                    <td className="py-3 px-4 text-wine-dark flex items-center gap-2">
                                        {t.kind === "credit" ? <ArrowDownCircle className="h-4 w-4 text-vineyard" /> : <ArrowUpCircle className="h-4 w-4 text-wine" />}
                                        {t.description || (t.kind === "credit" ? "Crédito" : "Utilização")}
                                    </td>
                                    <td className="text-xs text-wine-dark/60">{new Date(t.created_at).toLocaleDateString("pt-PT")}</td>
                                    <td className="text-xs text-wine-dark/60">{TX_STATUS[t.status] || t.status}</td>
                                    <td className={`text-right pr-4 font-heading ${t.kind === "credit" ? "text-vineyard" : "text-wine"}`}>{t.kind === "credit" ? "+" : "−"}€{t.amount.toFixed(2)}</td>
                                </tr>
                            ))}
                            {data.transactions.length === 0 && <tr><td colSpan="4" className="text-center py-8 text-wine-dark/60">Sem movimentos ainda.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>
        </section>
    );
}

function Stat({ label, value }) {
    return (
        <div>
            <span className="label-overline text-cream/50 text-[9px]">{label}</span>
            <p className="font-heading text-xl text-cream">{value}</p>
        </div>
    );
}
